import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MsmeImpactSidebar.css';
import MsmeImpactDropdown from './MsmeImpactDropdown';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parse } from 'date-fns';

const MsmeImpactSidebar = ({ onGenderChange, onAgeRangeChange, onCategoryChange, onDateRangeChange }) => {
  const [allData, setAllData] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [genderOptions, setGenderOptions] = useState([]);
  const [monthYearOptions, setMonthYearOptions] = useState([]);

  const [selectedFilters, setSelectedFilters] = useState({
    gender: '',
    ageRange: '',
    category: '',
    startMonth: '',
    endMonth: ''
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const response = await fetch('/db2_msmeimpact.json');
      const data = await response.json();
      setAllData(data);

      const categories = [...new Set(data.map(item => item['Category']))].filter(c => c);
      setCategoryOptions([...categories, 'Citizen Impacted']);

      const genders = [...new Set(data.map(item => item['Gender']))].filter(g => g);
      setGenderOptions(genders);

      const months = [...new Set(data.map(item => item['MonthYear']))].filter(m => m);
      setMonthYearOptions(months.sort());
    };
    fetchData();
  }, []);

  const handleGenderChange = (selectedGender) => {
    setSelectedFilters(prev => ({ ...prev, gender: selectedGender }));
    onGenderChange(selectedGender);
  };

  const handleAgeRangeChange = (selectedAgeRange) => {
    setSelectedFilters(prev => ({ ...prev, ageRange: selectedAgeRange }));
    onAgeRangeChange(selectedAgeRange);
  };

  const handleCategoryChange = (selectedCategory) => {
    setSelectedFilters(prev => ({ ...prev, category: selectedCategory }));
    if (selectedCategory === 'Citizen Impacted') {
      navigate('/citizen-impact');
    } else {
      onCategoryChange(selectedCategory);
    }
  };

  // ✅ Format & Parse using JSON’s YYYY-MM
  const formatMonthYear = (date) => format(date, 'yyyy-MM');
  const parseMonthYear = (monthYear) => parse(monthYear, 'yyyy-MM', new Date());

  // Restrict to dataset months only
  const allowedDates = monthYearOptions.map(m => parseMonthYear(m));

  const handleStartMonthChange = (date) => {
    const formatted = formatMonthYear(date);
    setSelectedFilters(prev => ({ ...prev, startMonth: formatted }));
    onDateRangeChange({ start: formatted, end: selectedFilters.endMonth });
  };

  const handleEndMonthChange = (date) => {
    const formatted = formatMonthYear(date);
    setSelectedFilters(prev => ({ ...prev, endMonth: formatted }));
    onDateRangeChange({ start: selectedFilters.startMonth, end: formatted });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      gender: '',
      ageRange: '',
      category: '',
      startMonth: '',
      endMonth: ''
    });
    onGenderChange('');
    onAgeRangeChange('');
    onCategoryChange('');
    onDateRangeChange({ start: '', end: '' });
  };

  return (
    <div className="msme-impact-sidebar">
      <h1 className="msme-impact-sidebar-header">Heat map based on</h1>

      <MsmeImpactDropdown
        label="MSME Reached"
        options={categoryOptions}
        onSelect={handleCategoryChange}
        selectedOption={selectedFilters.category}
      />

      <h3 className="msme-impact-sidebar-subheader">Filter Data By</h3>

      <nav>
        <ul>
          <li className="msme-impact-dropdown">
            <MsmeImpactDropdown
              label="Select Gender"
              options={genderOptions}
              onSelect={handleGenderChange}
              header="Gender"
              selectedOption={selectedFilters.gender}
            />
          </li>
          <li className="msme-impact-dropdown">
            <MsmeImpactDropdown
              label="Select Age Range"
              options={[...new Set(allData.map(item => item['Age_Range']))]}
              onSelect={handleAgeRangeChange}
              header="Age Range"
              selectedOption={selectedFilters.ageRange}
            />
          </li>

          {/* ✅ Start Month-Year Picker */}
          <li className="msme-impact-dropdown">
            <label>Start Month-Year</label>
            <DatePicker
              selected={selectedFilters.startMonth ? parseMonthYear(selectedFilters.startMonth) : null}
              onChange={handleStartMonthChange}
              dateFormat="yyyy-MM"
              showMonthYearPicker
              placeholderText="Select Start Month"
              includeDates={allowedDates}  // restrict to JSON months
              className="date-picker-input"
            />
          </li>

          {/* ✅ End Month-Year Picker */}
          <li className="msme-impact-dropdown">
            <label>End Month-Year</label>
            <DatePicker
              selected={selectedFilters.endMonth ? parseMonthYear(selectedFilters.endMonth) : null}
              onChange={handleEndMonthChange}
              dateFormat="yyyy-MM"
              showMonthYearPicker
              placeholderText="Select End Month"
              includeDates={allowedDates}  // restrict to JSON months
              className="date-picker-input"
            />
          </li>
        </ul>
      </nav>

      <button className="clear-all-filters-button" onClick={handleClearAllFilters}>
        Clear All Filters
      </button>
    </div>
  );
};

export default MsmeImpactSidebar;
